import { createFileRoute } from "@tanstack/react-router";
import { Lock, LockOpen, ShoppingCart, ArrowRight, Download, Award, CheckCircle2, AlertTriangle, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackButtonClick } from "@/lib/tracking";
import { BaixarEmentaDialog } from "@/components/ementas/BaixarEmentaDialog";
import { MinimalFooter } from "@/components/feo/MinimalFooter";
import type { ProductSlug } from "@/lib/ementas";

import logoProjeto from "@/assets/logo-projeto-fisio-completo.png";
import posOrtopedia from "@/assets/pos/thumb-ortopedia.webp";
import posDor from "@/assets/pos/thumb-dor.webp";
import posEsporte from "@/assets/pos/thumb-esporte.webp";
import posGeronto from "@/assets/pos/thumb-geronto.webp";
import posTraumato from "@/assets/pos/thumb-traumato.webp";
import posPrescricao from "@/assets/pos/thumb-prescricao.webp";
import bonusStarCursos from "@/assets/bonus-star-cursos.png";
import bonusFisioEmpreende from "@/assets/bonus-fisio-empreende.png";
import bonusFeoIa from "@/assets/bonus-feo-ia.png";
import bonusComunidade from "@/assets/bonus-comunidade.png";

const BONUSES = [
  {
    title: "Projeto Fisio Completo",
    image: bonusStarCursos,
    desc: "A maior biblioteca online de cursos em fisioterapia musculoesquelética.",
    points: ["+120 cursos com certificação", "+50 professores", "Atualizações mensais"],
  },
  {
    title: "Projeto Fisio Completo",
    image: bonusFisioEmpreende,
    desc: "A formação complementar\npara o fisio moderno.",
    points: ["Gestão de consultório e clínica", "Carreira e Posicionamento Digital", "Finanças pessoais"],
  },
  {
    title: "Projeto Fisio Completo",
    image: bonusFeoIa,
    desc: "Para apoiar seu raciocínio clínico e decisões baseadas em evidências.",
    points: ["Treinado com +1.000 horas de conteúdo FEO", "Baseada em evidências", "Manual de uso da ferramenta"],
  },
  {
    title: "Projeto Fisio Completo",
    image: bonusComunidade,
    desc: "Acesso à comunidade exclusiva\nde alunos FEO.",
    points: ["Networking com fisios de todo o Brasil", "Discussão de casos clínicos", "Aulas e mentorias exclusivas"],
  },
];

export const Route = createFileRoute("/fisiocompleto-venda")({
  head: () => ({
    meta: [
      { title: "Matrícula do Projeto Fisio Completo · Escolha a sua pós | Fisio em Ortopedia" },
      { name: "description", content: "Escolha a sua pós-graduação e garanta a condição especial do Projeto Fisio Completo." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Matrícula do Projeto Fisio Completo · Escolha a sua pós" },
      { property: "og:description", content: "Condição exclusiva para o grupo. Escolha a sua pós e finalize a matrícula." },
      { property: "og:url", content: "https://fisioemortopedia.com.br/fisiocompleto-venda" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://fisioemortopedia.com.br/fisiocompleto-venda" }],
  }),
  component: VendaPage,
});

type Pos = {
  name: string;
  product: ProductSlug;
  image: string;
  from: string;
  to: string;
  installments: string;
  buyUrl: string;
};

const POS: Pos[] = [
  {
    name: "Fisioterapia em Ortopedia e Trauma",
    product: "orto",
    image: posOrtopedia,
    from: "R$ 6.298,00",
    to: "R$ 1.597,00",
    installments: "10x sem juros de R$ 159,70",
    buyUrl: "https://sun.eduzz.com/60E2E8Q3W3?cupom=PFC&df=1&pf=1&np=10",
  },
  {
    name: "Especialização em Dor",
    product: "dor",
    image: posDor,
    from: "R$ 6.298,00",
    to: "R$ 1.597,00",
    installments: "10x sem juros de R$ 159,70",
    buyUrl: "https://sun.eduzz.com/Q9N25BN101?cupom=PFC&df=1&pf=1&np=10",
  },
  {
    name: "Fisioterapia em Esporte",
    product: "esporte",
    image: posEsporte,
    from: "R$ 6.298,00",
    to: "R$ 1.597,00",
    installments: "10x sem juros de R$ 159,70",
    buyUrl: "https://sun.eduzz.com/G96R1DJQW1?cupom=PFC&df=1&pf=1&np=10",
  },
  {
    name: "Fisioterapia em Gerontologia",
    product: "geronto",
    image: posGeronto,
    from: "R$ 6.298,00",
    to: "R$ 1.597,00",
    installments: "10x sem juros de R$ 159,70",
    buyUrl: "https://sun.eduzz.com/1W32Z6OG92?cupom=PFC&df=1&pf=1&np=10",
  },
  {
    name: "Fisioterapia em Traumatologia",
    product: "trauma",
    image: posTraumato,
    from: "R$ 3.998,00",
    to: "R$ 1.597,00",
    installments: "10x sem juros de R$ 159,70",
    buyUrl: "https://sun.eduzz.com/39YNDV54WO?cupom=PFC&df=1&pf=1&np=10",
  },
  {
    name: "Fisioterapia na Prescrição de Exercícios Terapêuticos",
    product: "prescricao",
    image: posPrescricao,
    from: "R$ 3.998,00",
    to: "R$ 1.597,00",
    installments: "10x sem juros de R$ 159,70",
    buyUrl: "https://sun.eduzz.com/R9JXJ6QE0X?cupom=PFC&df=1&pf=1&np=10",
  },
];

function VendaPage() {
  return (
    <main className="min-h-screen bg-background">
      <div className="bg-gradient-gold text-gold-foreground">
        <div className="container mx-auto flex items-center justify-center gap-2 px-4 py-2 text-center text-[11px] font-bold uppercase tracking-wider sm:text-xs">
          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
          <span>ATENÇÃO: RESTAM APENAS 12 VAGAS</span>
        </div>
      </div>
      {/* Header / Hero */}
      <section className="relative overflow-hidden bg-hero">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="container relative mx-auto max-w-5xl px-4 py-10 text-center sm:py-14">
          <img decoding="async"
            src={logoProjeto}
            alt="Projeto Fisio Completo"
            className="mx-auto h-12 w-auto sm:h-14"
          />
          <div className="mt-8 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-gold">
            <LockOpen className="h-3.5 w-3.5" /> Condição exclusiva · Grupo VIP
          </div>
          <p className="mt-3 text-xs font-medium text-muted-foreground sm:text-sm">
            * Exclusivo para fisioterapeutas formados
          </p>
          <h1 className="mt-6 text-balance text-3xl font-extrabold leading-tight text-foreground sm:text-5xl">
            Escolha a sua{" "}
            <span className="text-gradient-primary">pós-graduação</span>{" "}
            e garanta 4 bônus exclusivos.
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-balance text-base text-muted-foreground sm:text-lg">
            Clique em <strong className="text-foreground">Comprar agora</strong> na pós que combina com a próxima fase da sua carreira e ganhe <strong className="text-foreground">Star Cursos + Agente IA Fukusawa + Fisio Empreende + Comunidade FEO</strong>
          </p>
        </div>
      </section>

      {/* Catálogo */}
      <section className="bg-deep py-14 sm:py-20">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="grid grid-cols-1 gap-8 px-6 sm:px-0 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {POS.map((p) => (
              <article
                key={p.name}
                className="group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-card transition-transform duration-300 hover:-translate-y-1"
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-muted">
                  <img
                    src={p.image}
                    alt={p.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>

                <div className="flex flex-1 flex-col p-5">
                  <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
                    <ShoppingCart className="h-4 w-4 text-primary" />
                    <span>
                      De <span className="text-destructive line-through">{p.from}</span>{" "}
                      por <span className="font-bold text-foreground">{p.to}</span>
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">ou {p.installments}</p>

                  <div className="mt-5 flex flex-col gap-2 sm:flex-row">
                    <BaixarEmentaDialog product={p.product} posUrl={p.buyUrl}>
                      <Button
                        variant="outline"
                        className="flex-1 rounded-full border-border bg-transparent text-foreground hover:bg-secondary"
                        onClick={() => void trackButtonClick({ buttonId: `ementa_${p.product}`, buttonLabel: `Abrir formulário ementa, ${p.name}`, destination: `ementa-${p.product}-site` })}
                      >
                        <Download className="h-4 w-4" /> Baixar ementa
                      </Button>
                    </BaixarEmentaDialog>
                    <Button
                      asChild
                      className="flex-1 rounded-full bg-success text-primary-foreground hover:bg-success/90"
                    >
                      <a
                        href={p.buyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => void trackButtonClick({ buttonId: `comprar_${p.name}`, buttonLabel: `Comprar agora, ${p.name}`, destination: p.buyUrl })}
                      >
                        Comprar agora <ArrowRight className="h-4 w-4" />
                      </a>
                    </Button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* O que vem junto */}
      <section className="border-y border-border/40 bg-deep/60 py-14 sm:py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-gold">
              <Gift className="h-3 w-3" /> O que vem junto
            </span>
            <h2 className="mt-5 text-balance text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">
              Sua pós + <span className="text-gradient-primary">4 produtos exclusivos</span> que aceleram a sua carreira.
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
              Tudo o que um fisioterapeuta precisa para se especializar, se manter atualizado, atender melhor e empreender, em um só lugar!
            </p>
          </div>

          <div className="mx-auto mt-12 grid max-w-2xl gap-6 sm:grid-cols-2 lg:max-w-7xl lg:grid-cols-4">
            {BONUSES.map((b) => (
              <div
                key={b.title}
                className="group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-gradient-card p-6 transition hover:border-primary/50 hover:shadow-glow lg:aspect-auto aspect-square"
              >
                <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-primary/10 blur-2xl" />
                <div className="flex items-start justify-between">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-primary p-2 text-primary-foreground shadow-glow">
                    <img loading="lazy" decoding="async" src={b.image} alt={b.title} className="h-full w-full object-contain" />
                  </div>
                  <span className="inline-flex items-center gap-1 rounded-full border border-gold/40 bg-gold/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-gold">
                    <Lock className="h-3 w-3" /> Incluso
                  </span>
                </div>
                <h3 className="mt-5 text-xl font-bold tracking-tight">{b.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{b.desc}</p>
                <ul className="mt-5 space-y-2">
                  {b.points.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Surpresa no checkout — 5 certificações */}
      <section className="bg-deep pt-16 pb-10 sm:pt-20 sm:pb-14">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="relative overflow-hidden rounded-3xl border border-gold/30 bg-card/60 p-6 shadow-card sm:p-10">
            <div className="pointer-events-none absolute -inset-px rounded-3xl bg-gradient-to-br from-gold/10 via-transparent to-transparent" />
            <div className="relative grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-gold">
                  <Award className="h-3.5 w-3.5" /> SURPRESA NO CHECKOUT
                </div>
                <h2 className="mt-5 text-3xl font-extrabold leading-tight text-foreground sm:text-4xl">
                  E ainda tem <span className="text-gradient-primary">5 certificações</span> esperando por você no carrinho.
                </h2>
                <p className="mt-4 text-base text-muted-foreground">
                  Direto no checkout, você poderá adicionar todas as nossas certificações com uma{" "}
                  <strong className="text-foreground">condição especial</strong> reservada apenas para essa ação.
                  A chance perfeita de montar um currículo blindado.
                </p>
                <ul className="mt-6 space-y-2 text-sm text-foreground">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-success" /> 5 certificações disponíveis
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-success" /> Aplicação automática no carrinho
                  </li>
                </ul>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {["Lombar", "Cervical", "Agentes Eletro", "Ombro", "Joelho"].map((name) => (
                  <div
                    key={name}
                    className="flex flex-col items-center justify-center rounded-2xl border border-border bg-deep/60 p-4 text-center"
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gold/15">
                      <Award className="h-6 w-6 text-gold" />
                    </div>
                    <p className="mt-3 text-xs font-semibold uppercase tracking-wider text-foreground">
                      Certificação {name}
                    </p>
                    <span className="mt-2 inline-flex items-center gap-1 rounded-full border border-gold/40 bg-gold/10 px-2 py-0.5 text-[10px] font-semibold text-gold">
                      <Lock className="h-3 w-3" /> Oferta exclusiva
                    </span>
                  </div>
                ))}
                <div className="flex items-center justify-center rounded-2xl border border-dashed border-border bg-transparent p-4 text-center text-xs text-muted-foreground">
                  Disponível somente<br />no checkout
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dúvidas / WhatsApp */}
      <section className="bg-deep pt-10 pb-28 sm:pt-14 sm:pb-32">
        <div className="container mx-auto flex max-w-3xl flex-col items-center justify-center px-4 text-center">
          <h2 className="text-2xl font-bold text-foreground sm:text-3xl">
            Ficou alguma dúvida?
          </h2>
          <Button
            asChild
            size="lg"
            className="mt-6 rounded-full bg-success px-8 text-primary-foreground hover:bg-success/90"
          >
            <a href="https://nimble.li/qdgk72xm" target="_blank" rel="noopener noreferrer">
              Fale com nosso time no WhatsApp
            </a>
          </Button>
        </div>
      </section>

      <footer className="border-t border-border bg-background py-8 text-center text-xs text-muted-foreground">
      </footer>
      <MinimalFooter />
    </main>
  );
}
